import { ComponentProps, Fragment, useMemo } from 'react';
import { Button, Classes, H4, Switch, Tooltip } from '@blueprintjs/core';
import { classNames } from '@axo/shared/util/dom';
import { ace_hooks } from '@axo/shared/data-access/hooks';
import { ace } from '@axo/shared/data-access/types';
import { AceTable } from '../AceTable';
import { ApplicationLink } from '../ApplicationLink';
import { UserName } from '../UserName';
import { SmallTag } from '../SmallTag';
import { formatDatetimeShort } from '../../utils/formatDate';
import { usePersistedState } from '../../hooks/utils/usePersistedState';
import styles from './BucketDetailsPanel.module.css';

interface Props {
  id: string | undefined;
}

export function BucketDetailsPanel({ id }: Props) {
  const bucket = ace_hooks.useBucket(id ?? '');
  const summary = ace_hooks.useBucketSummary(id ?? '');
  const applications = ace_hooks.useBucketApplications(id ?? '');

  const [filterProcessed, setFilterProcessed] = usePersistedState(
    'ace.buckets.details.processed-filter.v1',
    true,
    window.sessionStorage,
  );

  const filtered = useMemo(
    () =>
      applications.data?.Items?.filter(
        (app) => !filterProcessed || app.ProcessedAt === null,
      ),
    [applications.data?.Items, filterProcessed],
  );

  return (
    <div className={styles.container}>
      <H4>
        <span className={classNames(bucket.isLoading && Classes.SKELETON)}>
          {bucket.data?.Name ?? '%bucket name%'}
        </span>
      </H4>

      <div className={styles.header}>
        <div>
          <p>
            <strong>Condition explanation</strong>
            <br />
            <span
              className={classNames(
                Classes.TEXT_MUTED,
                bucket.isLoading && Classes.SKELETON,
              )}
            >
              {bucket.data?.ConditionsExplanation ?? '%condition explanation%'}
            </span>
          </p>

          <p>
            <strong>Actions</strong>

            {bucket.data?.Actions?.map((action, _, items) => (
              <Fragment key={`${action.Name}-${action.Kind}`}>
                <br />
                <span className={Classes.TEXT_MUTED}>
                  {items.length > 1 && '- '}
                  {action.Name} - {action.Description}
                </span>
              </Fragment>
            )) ?? (
              <>
                <br />
                <span className={Classes.SKELETON}>%bucket actions%</span>
              </>
            )}
          </p>
        </div>

        <div className={styles.actions}>
          <SmallTag minimal intent="primary" icon="eye-open" round>
            <span className={classNames(summary.isLoading && Classes.SKELETON)}>
              Total {`${summary.data?.Total ?? '-'}`}
            </span>
          </SmallTag>

          <SmallTag minimal intent="success" icon="eye-open" round>
            <span className={classNames(summary.isLoading && Classes.SKELETON)}>
              Assigned {`${summary.data?.NumApplicationsAssigned ?? '-'}`}
            </span>
          </SmallTag>

          <SmallTag minimal intent="danger" icon="eye-open" round>
            <span className={classNames(summary.isLoading && Classes.SKELETON)}>
              Unassigned {`${summary.data?.NumApplicationsUnassigned ?? '-'}`}
            </span>
          </SmallTag>

          <Tooltip content="Refresh results">
            <Button
              icon="refresh"
              loading={applications.isFetching || summary.isFetching}
              disabled={applications.isIdle || summary.isIdle}
              variant="minimal"
              style={{ marginLeft: 'var(--margin-xsmall)' }}
              onClick={() => {
                applications.refetch();
                summary.refetch();
              }}
            />
          </Tooltip>
        </div>
      </div>

      <div className={styles.filter}>
        <Switch
          label="Hide processed"
          checked={filterProcessed}
          onChange={() => setFilterProcessed((prev) => !prev)}
        />
      </div>

      <AceTable<ace.ApplicationBucket>
        config={listConfig}
        uniqueRowIDKey="LoanApplicationID"
        loading={applications.isLoading}
        data={filtered ?? []}
      />
    </div>
  );
}

const listConfig: ComponentProps<
  typeof AceTable<ace.ApplicationBucket>
>['config'] = {
  columns: {
    LoanApplicationID: {
      label: 'Application',
      render: (_, app) => (
        <ApplicationLink id={`${app.LoanApplicationID ?? ''}`} />
      ),
    },
    AssigneeID: {
      label: 'Assigned to',
      render: (_, app) => <UserName clientID={`${app.AssigneeID ?? ''}`} />,
    },
    ProcessedBy: {
      label: 'Processed by',
      render: (_, app) => <UserName clientID={`${app.ProcessedBy ?? ''}`} />,
    },
    ProcessedAt: {
      label: 'Processed at',
      render: (_, app) => formatDatetimeShort(app.ProcessedAt),
    },
  },
  extra: [
    // Added placeholder to make sure last column is left aligned.
    {
      label: '',
      key: 'placeholder',
      render: () => null,
    },
  ],
};
